import { useState } from "react";
import { supportTickets } from "../../../mocks/adminData";
import type { SupportTicket } from "../../../mocks/adminData";

const STATUS_CONFIG = {
  open:        { label: "Açık",         class: "bg-red-100 text-red-700" },
  in_progress: { label: "İşlemde",      class: "bg-amber-100 text-amber-700" },
  resolved:    { label: "Çözüldü",      class: "bg-emerald-100 text-emerald-700" },
  closed:      { label: "Kapatıldı",    class: "bg-stone-100 text-stone-500" },
};
const PRIORITY_CONFIG = {
  high:   { label: "Yüksek", class: "text-red-600" },
  medium: { label: "Orta",   class: "text-amber-600" },
  low:    { label: "Düşük",  class: "text-stone-400" },
};
const CATEGORY_LABELS: Record<string, string> = {
  technical: "Teknik",
  billing: "Fatura",
  content: "İçerik",
  general: "Genel",
};

export default function SupportSection() {
  const [tickets, setTickets] = useState(supportTickets);
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState<SupportTicket | null>(null);

  const filtered = tickets.filter((t) => filterStatus === "all" || t.status === filterStatus);

  const updateStatus = (id: string, status: SupportTicket["status"]) => {
    setTickets((prev) => prev.map((t) => t.id === id ? { ...t, status } : t));
    if (selected?.id === id) setSelected((prev) => prev ? { ...prev, status } : null);
  };

  const openCount = tickets.filter((t) => t.status === "open").length;

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl p-4 border border-stone-100 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {["all", "open", "in_progress", "resolved", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filterStatus === s ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"
              }`}
            >
              {s === "all" ? "Tümü" : STATUS_CONFIG[s as keyof typeof STATUS_CONFIG]?.label}
              {s === "open" && openCount > 0 && (
                <span className="ml-1.5 w-4 h-4 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs">{openCount}</span>
              )}
            </button>
          ))}
        </div>
        <span className="text-xs text-stone-400 ml-auto">{filtered.length} talep</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Ticket List */}
        <div className="space-y-3">
          {filtered.map((ticket) => {
            const statConf = STATUS_CONFIG[ticket.status];
            const prioConf = PRIORITY_CONFIG[ticket.priority];
            return (
              <div
                key={ticket.id}
                onClick={() => setSelected(ticket)}
                className={`bg-white rounded-2xl p-4 border cursor-pointer transition-all hover:border-stone-300 ${
                  selected?.id === ticket.id ? "border-emerald-400" : "border-stone-100"
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-stone-400 font-mono">{ticket.ticketNo}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statConf.class}`}>{statConf.label}</span>
                  </div>
                  <span className={`text-xs font-semibold ${prioConf.class} flex items-center gap-1`}>
                    <i className="ri-flag-line text-xs" /> {prioConf.label}
                  </span>
                </div>
                <p className="font-medium text-stone-800 text-sm mb-1">{ticket.subject}</p>
                <p className="text-xs text-stone-500 line-clamp-1">{ticket.message}</p>
                <div className="flex items-center justify-between mt-2 text-xs text-stone-400">
                  <span>{ticket.userName} · {CATEGORY_LABELS[ticket.category]}</span>
                  <span>{ticket.createdAt}</span>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="bg-white rounded-2xl p-12 text-center border border-stone-100 text-stone-400 text-sm">
              Bu kategoride talep yok
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="bg-white rounded-2xl border border-stone-100 sticky top-4">
          {selected ? (
            <div className="p-5">
              <div className="flex items-start justify-between gap-2 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-stone-400">{selected.ticketNo}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[selected.status].class}`}>
                      {STATUS_CONFIG[selected.status].label}
                    </span>
                  </div>
                  <h3 className="font-bold text-stone-800">{selected.subject}</h3>
                </div>
              </div>

              {/* User info */}
              <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl mb-4">
                <div className="w-9 h-9 rounded-full bg-emerald-700 flex items-center justify-center text-white text-xs font-bold">
                  {selected.userName.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-800">{selected.userName}</p>
                  <p className="text-xs text-stone-400">{selected.userEmail}</p>
                </div>
              </div>

              {/* Message */}
              <div className="bg-stone-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-stone-600 leading-relaxed">{selected.message}</p>
                <p className="text-xs text-stone-400 mt-2">{selected.createdAt}</p>
              </div>

              {/* Reply area */}
              <textarea
                placeholder="Yanıt yaz..."
                rows={3}
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 resize-none mb-3"
              />

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => updateStatus(selected.id, "in_progress")}
                  className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  İşleme Al
                </button>
                <button
                  onClick={() => updateStatus(selected.id, "resolved")}
                  className="flex-1 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Çözüldü İşaretle
                </button>
                <button
                  onClick={() => updateStatus(selected.id, "closed")}
                  className="py-2 px-3 border border-stone-200 text-stone-500 rounded-xl text-xs font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Kapat
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-stone-400">
              <i className="ri-customer-service-2-line text-4xl mb-3 text-stone-200" />
              <p className="text-sm">Detayları görmek için bir talep seçin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
